import { Heading, Input, PageGridContainer, Tag, TagCloseButton, TagText } from "@govlaunch/web";
import debounce from "lodash/fp/debounce";
import { useRouter } from "next/router";
import React, { useCallback, useRef, useState } from "react";
import stripUndefined from "~/lib/utils/stripUndefined";
import TagsFilterSelect from "~/src/shared/components/TagsFilterSelect";
import useTagsFilter from "~/src/shared/hooks/useTagsFilter";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import stringOrFirst from "~/src/utils/stringOrFirst";
import { FormattedMessage, useIntl } from "react-intl";

export default function ProductsListingPageFilters() {
  const router = useRouter();
  const intl = useIntl();

  const variables = {
    name: stringOrFirst(router.query.name),
    tagSlugs: Array.isArray(router.query.tags) ? router.query.tags : router.query.tags ? [router.query.tags] : [],
  };

  const initialTagSlugs = useRef(variables.tagSlugs);

  const { loading, tags } = useTagsFilter({
    tags: initialTagSlugs.current,
  });

  const [tagOptions, setTagOptions] = useState(tags);

  const commitSearch = useCallback((searchQuery: string) => {
    router.push({
      pathname: router.pathname,
      query: stripUndefined({
        ...router.query,
        name: searchQuery,
      }),
    });
  }, []);

  const performSearch = useCallback(
    debounce(400, (searchQuery: string) => {
      commitSearch(searchQuery);
    }),
    [],
  );

  if (loading) {
    return null;
  }

  return (
    <>
      <PageGridContainer
        gridTemplateColumns={["1fr", "1fr", "max-content 1fr 1fr", "max-content 1fr 1fr"]}
        pb={[0, 0, 4]}
        borderBottom={["none", "none", "1px solid #ADB6C3"]}
        spacing={4}
        mb={6}
        borderBottomColor="gray.300"
        mt={6}
      >
        <Heading color="gray.0" textTransform="uppercase" fontSize="2xl" as="h1">
          <FormattedMessage defaultMessage="Products" id="7NFfmz" />
        </Heading>

        <TagsFilterSelect
          tagTypes={["PRODUCT"]}
          disabled={loading}
          selectedItems={tagOptions}
          onChange={(tags) => {
            setTagOptions(tags);

            router.push(
              appendNextLinkHrefParams(router, {
                tags: tags.map((tag) => tag.value),
              }),
            );
          }}
        />

        <Input
          aria-label={intl.formatMessage({
            defaultMessage: "Product search",
            id: "xMxkKr",
          })}
          size="sm"
          placeholder={intl.formatMessage({
            defaultMessage: "Search for a product...",
            id: "ryBIxb",
          })}
          defaultValue={variables.name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            performSearch(event.target.value);
          }}
        />
      </PageGridContainer>

      {tagOptions.length > 0 && (
        <PageGridContainer mt={4}>
          <div>
            {tagOptions
              .filter((tag) => variables.tagSlugs.includes(tag.value))
              .map((tag) => (
                <Tag key={tag.value} selected={true} mr={3} mb={2}>
                  <TagText>{tag.name}</TagText>

                  <TagCloseButton
                    onClick={() => {
                      const filteredTags = tagOptions.filter((candidate) => candidate.value !== tag.value);

                      setTagOptions(filteredTags);

                      router.push(
                        appendNextLinkHrefParams(router, {
                          tags: filteredTags.map((tag) => tag.value),
                        }),
                      );
                    }}
                  />
                </Tag>
              ))}
          </div>
        </PageGridContainer>
      )}
    </>
  );
}
